import { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_imer-luxury/artifacts/2ygeu2r0_WhatsApp%20Image%202026-04-12%20at%2017.07.32.jpeg';

const Logo = () => (
  <Link to="/" className="flex items-center group" aria-label="İMER Events & Weddings">
    <div
      className="transition-all duration-300 group-hover:opacity-90"
      style={{
        background: 'rgba(255,255,255,0.96)',
        padding: '5px 10px',
        borderRadius: '3px',
        border: '1px solid rgba(201,168,76,0.3)',
        boxShadow: '0 2px 12px rgba(201,168,76,0.12)',
      }}
    >
      <img
        src={LOGO_URL}
        alt="İMER Events & Weddings"
        style={{ height: '46px', width: 'auto', objectFit: 'contain', display: 'block' }}
      />
    </div>
  </Link>
);

const services = [
  { label: 'Kurumsal Etkinlikler', to: '/services/corporate', desc: 'Gala, zirve, lansman' },
  { label: 'Düğün', to: '/services/wedding', desc: 'Lüks düğün deneyimi' },
  { label: 'Açılış & Lansman', to: '/services/launch', desc: 'Marka etkinlikleri' },
  { label: 'Özel Davetler', to: '/services/private', desc: 'Kişisel kutlamalar' },
];

export default function Header() {
  const scrollY = useMotionValue(0);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      scrollY.set(y);
      setScrolled(y > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Ana Sayfa', to: '/' },
    { label: 'Hakkımızda', to: '/about' },
    { label: 'Portföy', to: '/portfolio' },
    { label: 'İletişim', to: '/contact' },
  ];

  return (
    <>
      <motion.header
        data-testid="header-nav"
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          backgroundColor: scrolled ? 'rgba(7,7,8,0.93)' : 'rgba(7,7,8,0)',
          backdropFilter: scrolled ? 'blur(20px)' : 'blur(0px)',
          borderBottom: scrolled ? '1px solid rgba(201,168,76,0.12)' : '1px solid rgba(201,168,76,0)',
        }}
      >
        <div className="container-luxury">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Logo />

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `font-body text-sm tracking-[0.06em] transition-colors duration-200 ${isActive ? 'text-[#C9A84C]' : 'text-[rgba(245,240,232,0.78)] hover:text-[#F5F0E8]'}`
                  }
                  end={link.to === '/'}
                >
                  {link.label}
                </NavLink>
              ))}

              {/* Services Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
                data-testid="header-services-menu"
              >
                <button className="flex items-center gap-1 font-body text-sm tracking-[0.06em] text-[rgba(245,240,232,0.78)] hover:text-[#F5F0E8] transition-colors duration-200">
                  Hizmetler
                  <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-64"
                    >
                      <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(14,15,18,0.97)', border: '1px solid rgba(201,168,76,0.15)', backdropFilter: 'blur(20px)' }}>
                        {services.map((service) => (
                          <Link
                            key={service.to}
                            to={service.to}
                            onClick={() => setServicesOpen(false)}
                            className="flex flex-col px-5 py-3.5 transition-colors duration-200 hover:bg-[rgba(201,168,76,0.06)] border-b last:border-b-0"
                            style={{ borderColor: 'rgba(201,168,76,0.08)' }}
                          >
                            <span className="font-body text-sm text-[#F5F0E8] font-medium">{service.label}</span>
                            <span className="font-body text-xs text-[rgba(245,240,232,0.5)] mt-0.5">{service.desc}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            {/* CTA + Mobile */}
            <div className="flex items-center gap-4">
              <Link
                to="/quote"
                className="hidden md:inline-flex items-center btn-gold rounded-none text-xs tracking-[0.12em] uppercase h-10 px-6"
                data-testid="header-get-quote-button"
              >
                Teklif Al
              </Link>

              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 text-[rgba(245,240,232,0.78)] hover:text-[#F5F0E8] transition-colors"
                aria-label="Menüyü Aç"
                data-testid="header-mobile-menu-button"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] mobile-menu-overlay flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-5">
              <Logo />
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-[rgba(245,240,232,0.78)] hover:text-[#F5F0E8]"
                aria-label="Menüyü Kapat"
              >
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 flex flex-col justify-center px-8 gap-1">
              <span className="overline mb-6">Navigasyon</span>
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.07, duration: 0.4, ease: [0.22,1,0.36,1] }}
                >
                  <NavLink
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    end={link.to === '/'}
                    className={({ isActive }) =>
                      `block font-display text-3xl font-medium py-3 transition-colors ${isActive ? 'text-[#C9A84C]' : 'text-[#F5F0E8]'}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="mt-2"
              >
                <span className="font-display text-3xl font-medium text-[#F5F0E8] py-3 block">Hizmetler</span>
                <div className="pl-4 flex flex-col gap-1 mt-1">
                  {services.map((service, i) => (
                    <motion.div
                      key={service.to}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.35 + i * 0.05 }}
                    >
                      <Link
                        to={service.to}
                        onClick={() => setMobileOpen(false)}
                        className="block font-body text-lg text-[rgba(245,240,232,0.7)] hover:text-[#C9A84C] py-1.5 transition-colors"
                      >
                        {service.label}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            <div className="px-8 pb-10">
              <Link
                to="/quote"
                onClick={() => setMobileOpen(false)}
                className="btn-gold w-full text-center block py-4 text-sm tracking-[0.12em] uppercase"
              >
                Teklif Al
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
